import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  FILE_MIME_TYPES,
  FILE_MIME_TYPE_EXTENSIONS,
  VALID_IMAGE_FILE_MIME_TYPES,
  VIDEO_FILE_MIME_TYPES,
  WORD_MIME_TYPES,
} from "@miplatform/constants";
import { getRootUrl } from "@miplatform/utils";
import { mdiFileDocumentOutline, mdiImageOffOutline } from "@mdi/js";
import { Box, Link, Typography } from "@mui/material";
import { DocumentThumbnail } from "../DocumentThumbnail/DocumentThumbnail";
import { MiIcon } from "../MiIcon/MiIcon";

export const DocumentView = ({ document = {} }) => {
  const { classes, theme } = useStyles();
  const API_URL = getRootUrl();
  const documentUrl = document.tempURL
    ? document.tempURL
    : `${API_URL}/${document.referenceId}`;

  if (!document.referenceId && !document.tempURL) {
    return (
      <Box padding={5} position="relative" height="100%">
        <MiIcon
          path={mdiImageOffOutline}
          size={1.5}
          color={theme.palette.grey.main}
        />
      </Box>
    );
  }

  if (WORD_MIME_TYPES.includes(document.contentType) && !document.tempURL) {
    const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
      documentUrl
    )}&embedded=true`;

    return (
      <iframe
        src={googleViewerUrl}
        className={classes.wordPreview}
        width="100%"
        height="100%"
        title="Word Document Viewer"
        allowFullScreen
      />
    );
  }

  if (FILE_MIME_TYPES.PDF === document.contentType) {
    return (
      <object
        data={documentUrl}
        type="application/pdf"
        className={classes.pdfPreview}
        aria-label="PDF document"
      />
    );
  }

  if (VIDEO_FILE_MIME_TYPES.includes(document.contentType)) {
    return (
      <video controls width="100%" height="100%" src={documentUrl}>
        <track kind="captions" default />
      </video>
    );
  }

  if (VALID_IMAGE_FILE_MIME_TYPES.includes(document.contentType)) {
    return (
      <img
        alt=""
        src={document.referenceId ? documentUrl : document.tempURL}
        className={classes.previewImage}
      />
    );
  }

  return (
    <Box
      padding={4}
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      borderRadius={theme.shape.borderRadius}
      backgroundColor={theme.palette.primary.light}
    >
      <MiIcon
        path={mdiFileDocumentOutline}
        size={2.5}
        color={theme.palette.primary.main}
        style={{ marginBottom: theme.spacing(2) }}
      />
      <Typography fontWeight="600" gutterBottom>
        Preview Not Available
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: theme.spacing(2) }}
      >
        {`The file type "${
          FILE_MIME_TYPE_EXTENSIONS[document.contentType] ||
          document.contentType
        }" is not supported for viewing.`}
      </Typography>
      <Link
        href={documentUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="body1"
        color="primary.dark"
      >
        Click here to download the document.
      </Link>
    </Box>
  );
};

/**
 * DocumentViewer component to display documents with fullscreen and zoom capabilities.
 *
 * @param {Object} [props] - The properties object.
 * @param {boolean} [props.fullScreenStatus] - The status indicating if fullscreen mode is active.
 * @param {function} [props.onUnfullscreen] - The function to call when exiting fullscreen mode.
 * @param {function} [props.onDelete] - The function to call when deleting a document.
 * @param {Array<any>} [props.documents = []] - The array of document objects with referenceId and contentType.
 * @returns {JSX.Element} The rendered DocumentViewer component.
 */

export const DocumentViewer = (props) => {
  const { fullScreenStatus, onUnfullscreen, onDelete, documents = [] } = props;
  const [zoomedIn, setZoomedIn] = useState(false);
  const [fullScreenActive, setFullScreenActive] = useState(false);
  const [activeDocument, setActiveDocument] = useState(0);

  const { classes: miClasses } = useStyles();

  useEffect(() => {
    if (fullScreenStatus) {
      fullScreen();
      setActiveDocument(0);
    } else if (!fullScreenStatus) {
      unFullScreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenStatus]);

  useEffect(() => {
    if (!documents[activeDocument]) {
      setActiveDocument(0);
    }
  }, [documents, activeDocument]);

  const fullScreen = () => {
    setZoomedIn(true);
    setTimeout(() => {
      setFullScreenActive(true);
    }, 200);
  };

  const unFullScreen = () => {
    setZoomedIn(false);
    setTimeout(() => {
      onUnfullscreen();
      setFullScreenActive(false);
    }, 200);
  };

  const handleClick = (event, index) => {
    event.stopPropagation();
    setActiveDocument(index);
  };

  if (!fullScreenActive) {
    return null;
  }

  return (
    <Box
      style={{
        animation: zoomedIn
          ? "$fadeIn .3s ease-in-out"
          : "$fadeOut .3s ease-out",
      }}
      onClick={(e) => {
        e.stopPropagation();
        unFullScreen();
      }}
      className={miClasses.container}
    >
      <Box
        id="scaleWrapper"
        className={zoomedIn ? miClasses.scaleIn : miClasses.scaleOut}
        onClick={(e) => e.stopPropagation()}
      >
        <DocumentView document={documents[activeDocument]} />
      </Box>
      {(documents.length > 1 || onDelete) && (
        <Box className={miClasses.documentsContainer}>
          {documents.map((document, index) => (
            <DocumentThumbnail
              key={document.referenceId || document.tempURL}
              document={document}
              onDelete={onDelete ? () => onDelete(document, index) : undefined}
              onClick={(event) => handleClick(event, index)}
              dimension={80}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    zIndex: theme.zIndex.popper,
    width: "100%",
    height: "100%",
    position: "fixed",
    top: 0,
    left: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.5)",
  },
  videoIcon: {
    position: "absolute",
    top: "45%",
    left: "45%",
    zIndex: theme.zIndex.appBar,
    opacity: 0.8,
  },
  scaleIn: {
    animation: "$scaleIn2 .3s ease-in-out",
    flex: 1,
    maxHeight: "70%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("lg")]: { maxHeight: "60%" },
  },
  scaleOut: {
    animation: "$scaleOut2 .3s ease-in-out",
    maxHeight: "70%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    [theme.breakpoints.down("lg")]: { maxHeight: "60%" },
  },
  previewImage: {
    boxShadow: "1rem 1rem 1rem rgba(0,0,0,0.7)",
    maxHeight: "100%",
    maxWidth: "100%",
  },
  documentsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: theme.spacing(2),
    overflowX: "auto",
    margin: 20,
    width: "100%",
  },
  pdfPreview: {
    height: "100%",
    width: 700,
    [theme.breakpoints.down("sm")]: { width: 300 },
  },
  wordPreview: {
    height: "100%",
    width: 700,
    border: 0,
    [theme.breakpoints.down("sm")]: { width: 300 },
  },
  "@keyframes fadeIn": { "0%": { opacity: 0 }, "100%": { opacity: 0.6 } },
  "@keyframes fadeOut": { "0%": { opacity: 0.6 }, "100%": { opacity: 0 } },
  "@keyframes scaleIn2": {
    "0%": { transform: "scale(0.5)" },
    "100%": { transform: "scale(1)" },
  },
  "@keyframes scaleOut2": {
    "0%": { transform: "scale(1)" },
    "100%": { transform: "scale(0.5)" },
  },
}));
